"use client";

// Access & Permissions tab for the admin-user registry detail modal.
//
// USER_ROLES_AND_ACCESS: which panel shows is driven by the user's ROLE
// (resolveUserRole/isClientRole over app_users.role_id) — there is no
// is_client/is_employee column. Access itself lives in the per-contact and
// per-company grant tables, which is why a client user gets the company+contact
// grants summary and an employee gets the contact-grant list.
// Split out of AdminUserDetailTabComponents.tsx (module-size).

import React from "react";
import { EmptyState, useGlobalModalStack, MODAL_IDS } from "@west-tennessee-consulting/ui";
import { ShieldAlert, RefreshCw, UserPlus, Users as UsersIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { resolveUserRole, isClientRole } from "@/lib/constants/user-types";
import { GrantContactAccessModal } from "@/components/admin/access/GrantClientAccessModal";
import { GrantEmployeeAccessModal } from "@/components/admin/access/GrantEmployeeAccessModal";
import { useUserAccessGrants } from "@/components/admin/users/UserDetailModal/use-user-access-grants";
import { ClientAccessGrantsSummary } from "@/components/admin/access/ClientAccessGrantsSummary";
import {
  useCompanyClientAccess,
  useClientUserAccess,
} from "@/lib/hooks/api/admin/sections/client-access";
import { AccessGrantList } from "@/components/admin/users/UserDetailModal/components/AccessGrantList";
import type { AdminUserTabProps } from "./AdminUserFields";

export function AdminUserAccessDisplay({ item: user }: AdminUserTabProps) {
  const { openModal } = useGlobalModalStack();
  const grants = useUserAccessGrants(user.id);
  const role = resolveUserRole(user);
  const isClient = isClientRole(role);

  // Client-portal grants are only fetched for a CLIENT user — an employee has
  // none, so asking for them would be a wasted round trip per tab open.
  const companyAccess = useCompanyClientAccess(isClient ? user.id : null);
  const contactAccess = useClientUserAccess(isClient ? user.id : null);

  const [showGrantClient, setShowGrantClient] = React.useState(false);
  const [showGrantEmployee, setShowGrantEmployee] = React.useState(false);

  const openContact = React.useCallback(
    (contactId: string) => {
      openModal(MODAL_IDS.contacts.detail, { contactsId: contactId });
    },
    [openModal]
  );

  const openCompany = React.useCallback(
    (companyId: string) => {
      openModal(MODAL_IDS.companies.detail, { companyId });
    },
    [openModal]
  );

  // Stable retry handler — EmptyState's action Button is memo'd (MEMO_STABLE_PROPS).
  const handleRetryGrants = React.useCallback(() => grants.refetch(), [grants.refetch]);

  // MOUNT_GATE_MUST_NOT_HIDE_DATA — only show the skeleton while there is
  // genuinely nothing to render; a refetch must not blank a populated panel.
  const isLoading = grants.isLoading && !grants.data;
  const clientGrantsLoading =
    isClient &&
    ((companyAccess.isLoading && !companyAccess.data) ||
      (contactAccess.isLoading && !contactAccess.data));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => setShowGrantClient(true)}>
          <UserPlus className="h-4 w-4 mr-2" />
          Grant Client Access
        </Button>
        <Button variant="outline" size="sm" onClick={() => setShowGrantEmployee(true)}>
          <UsersIcon className="h-4 w-4 mr-2" />
          Grant Employee Access
        </Button>
      </div>

      {grants.isError ? (
        <EmptyState
          icon={ShieldAlert}
          title="Couldn't load access grants"
          description="We weren't able to load this user's current access."
          action={
            <Button onClick={handleRetryGrants}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          }
        />
      ) : isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-12 rounded-md bg-muted/50 animate-pulse" />
          ))}
        </div>
      ) : isClient ? (
        <ClientAccessGrantsSummary
          companyGrants={companyAccess.data ?? []}
          contactGrants={contactAccess.data ?? []}
          isLoading={clientGrantsLoading}
          onOpenCompany={openCompany}
          onOpenContact={openContact}
          emptyMessage="This client user can't see any contacts yet. Use Grant Client Access above."
        />
      ) : (
        <AccessGrantList
          title="Contacts this employee can access"
          emptyTitle="No contact access granted"
          emptyDescription="This employee has no contact-level access grants. Use Grant Employee Access above."
          grants={grants.data?.employeeGrants ?? []}
          onOpenContact={openContact}
        />
      )}

      {/* Grant-access modals — reuse the existing admin modals. */}
      <GrantContactAccessModal
        isOpen={showGrantClient}
        onClose={() => setShowGrantClient(false)}
        user={user}
        onSuccess={async () => {
          setShowGrantClient(false);
          await grants.refetch();
        }}
      />
      <GrantEmployeeAccessModal
        isOpen={showGrantEmployee}
        onClose={() => setShowGrantEmployee(false)}
        user={user}
        onSuccess={async () => {
          setShowGrantEmployee(false);
          await grants.refetch();
        }}
      />
    </div>
  );
}
